import {
  CircularProgress,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import { CancelButton } from "./components/cancel/CancelButton";
import {
  AccountBox,
  Public,
  Label,
  ReceiptLong,
  EditNote,
  Flag,
  PriceCheck,
} from "@mui/icons-material";
import { useTranslation } from "../../../../hooks/useTranslation";
import { useMarketplaceStore } from "../../../../store/useMarketplaceStore";
import { Order } from "../../../orders/utils/types";
import { useState } from "react";
import { AmazonViewDialog } from "../../../orders/components/utils/AmazonViewDialog";
import { ShippingLabel } from "../ShippingLabel";
import { InvoicePreviewModal } from "../invoice/InvoicePreviewModal";
import { OrderAction } from "./components/OrderAction";
import { AddMessageDialog } from "./components/AddMessageDialog";
import { AssignFlag } from "../../../../components/flag/components/assign/AssignFlag";
import UnassignFlag from "../../../../components/flag/components/assign/UnassignFlag";
import { AlreadyPurchasedDialog } from "./components/AlreadyPurchasedDialog";

const openPage = (url: string) => window.open(url, "_blank");

type Props = {
  order: Order;
};

type OrderItem = {
  id: string | number;
  title: string;
  asin: string;
  imageUrl?: string;
  quantityOrdered: number;
  itemPrice: number;
};

export const Sidebar = ({ order }: Props) => {
  const selectedCountry = useMarketplaceStore((state) => state.selectedCountry);
  const [amazonViewDialogOpen, setAmazonViewDialogOpen] =
    useState<boolean>(false);
  const [shippingLabelDialogOpen, setShippingLabelDialogOpen] =
    useState<boolean>(false);
  const [removeFlagDialogOpen, setRemoveFlagDialogOpen] =
    useState<boolean>(false);

  const [assignFlagDialogOpen, setAssignFlagDialogOpen] =
    useState<boolean>(false);
  const [alreadyPurchasedDialogOpen, setAlreadyPurchasedDialogOpen] =
    useState(false);

  const [invoicePreviewOpen, setInvoicePreviewOpen] = useState<boolean>(false);
  const { translate, translateWithoutPrefix } = useTranslation(
    "pages.orders.detail.sidebar",
  );
  const [messageDialogOpen, setMessageDialogOpen] = useState(false);

  const orderItemsWithImages = order.orderItems?.map(
    (item: OrderItem, index: number) => ({
      ...item,
      imageUrl: order.images?.[index] ?? undefined,
    }),
  );

  if (!(order && selectedCountry?.countryCode)) {
    return <CircularProgress />;
  }

  return (
    <List dense>
      <ListItemButton
        onClick={() => {
          setInvoicePreviewOpen(true);
        }}
      >
        <ListItemIcon>
          <ReceiptLong />
        </ListItemIcon>
        <ListItemText primary={translate("invoicePreview")} />
      </ListItemButton>

      <ListItemButton
        onClick={() => {
          openPage(
            `${selectedCountry.sellerCentralURL}/orders-v3/order/${order.amazonOrderId}`,
          );
        }}
      >
        <ListItemIcon>
          <AccountBox />
        </ListItemIcon>
        <ListItemText primary={translate("sellerCentralView")} />
      </ListItemButton>

      <ListItemButton
        onClick={() => {
          setAmazonViewDialogOpen(true);
        }}
      >
        <ListItemIcon>
          <Public />
        </ListItemIcon>
        <ListItemText primary={translate("amazonView")} />
      </ListItemButton>

      <ListItemButton onClick={() => setMessageDialogOpen(true)}>
        <ListItemIcon>
          <EditNote />
        </ListItemIcon>
        <ListItemText
          primary={translateWithoutPrefix(
            "pages.orders.detail.notes.addMessage",
          )}
        />
      </ListItemButton>
      {order.orderFlag ? (
        <ListItemButton
          onClick={() => {
            setRemoveFlagDialogOpen(true);
          }}
        >
          <ListItemIcon>
            <Flag />
          </ListItemIcon>
          <ListItemText
            primary={translateWithoutPrefix(
              "pages.orders.list.fields.actions.flag.unassign",
            )}
          />
        </ListItemButton>
      ) : (
        <ListItemButton
          onClick={() => {
            setAssignFlagDialogOpen(true);
          }}
        >
          <ListItemIcon>
            <Flag />
          </ListItemIcon>
          <ListItemText
            primary={translateWithoutPrefix(
              "pages.orders.list.fields.actions.flag.assign",
            )}
          />
        </ListItemButton>
      )}
      {order.deliveryOrder?.shipment?.labelUrl && (
        <ListItemButton
          onClick={() => {
            setShippingLabelDialogOpen(true);
          }}
        >
          <ListItemIcon>
            <Label />
          </ListItemIcon>
          <ListItemText primary={translate("label")} />
        </ListItemButton>
      )}
      {(order.orderItems?.some(
        (item) => item.quantityOrdered !== item.quantityBought,
      ) ||
        order.orderStatus === "WAITING_FOR_PURCHASE" ||
        order.orderStatus === "CANCELLED" ||
        order.orderStatus === "WAITING_FOR_ADDRESS") && (
        <OrderAction order={order} />
      )}

      <ListItemButton onClick={() => setAlreadyPurchasedDialogOpen(true)}>
        <ListItemIcon>
          <PriceCheck />
        </ListItemIcon>
        <ListItemText primary={translate("alreadyPurchased.title")} />
      </ListItemButton>

      {order.orderStatus !== "CANCELLED" && <CancelButton />}

      <ShippingLabel
        open={shippingLabelDialogOpen}
        onClose={() => setShippingLabelDialogOpen(false)}
        labelUrl={order.deliveryOrder?.shipment?.labelUrl}
      />

      <InvoicePreviewModal
        open={invoicePreviewOpen}
        onClose={() => setInvoicePreviewOpen(false)}
        order={order}
      />

      <AmazonViewDialog
        open={amazonViewDialogOpen}
        onClose={() => setAmazonViewDialogOpen(false)}
        orderItems={orderItemsWithImages}
      />

      <AddMessageDialog
        open={messageDialogOpen}
        onClose={() => setMessageDialogOpen(false)}
      />
      {order.orderFlag && removeFlagDialogOpen && (
        <UnassignFlag
          open={removeFlagDialogOpen}
          onClose={() => setRemoveFlagDialogOpen(false)}
          flag={order.orderFlag}
          orderId={order.id}
        />
      )}
      {assignFlagDialogOpen && (
        <AssignFlag
          open={assignFlagDialogOpen}
          onClose={() => setAssignFlagDialogOpen(false)}
          orderId={order.id}
        />
      )}
      <AlreadyPurchasedDialog
        open={alreadyPurchasedDialogOpen}
        onClose={() => setAlreadyPurchasedDialogOpen(false)}
        order={order}
      />
    </List>
  );
};
